import { FileWithPath } from '@mantine/dropzone';
import i18n from 'i18n';
import { ServerError } from 'shared/constants/server-errors';
import { GoogleDriveFileInfo, GoogleDriveResourceFile } from 'shared/interfaces/GoogleDrive';
import { ShopifyResourceConfig, ShopifyResourceConfigWithStore } from 'shared/interfaces/ShopifyStore';
import { Website } from 'shared/interfaces/Website';
import { YoutubeVideo } from 'shared/interfaces/Youtube';

export enum ResourceType {
  VIDEO = 'VIDEO',
  PDF = 'PDF',
  TEXT = 'TEXT',
  YOUTUBE = 'YOUTUBE',
  WEB = 'WEB',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  SHOPIFY_STORE = 'SHOPIFY_STORE',
  NULL = 'NULL'
}

export enum ResourceAiProcessingStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  FAILED_NO_TEXT_FOUND = 'FAILED_NO_TEXT_FOUND',
  FAILED = 'FAILED'
}

export interface Resource {
  _id: string;
  organisation: string;
  creator: string;
  teams: string[];
  fileName: string;
  type: ResourceType;
  fileKey: string;
  createdAt: number;
  aiProcessingStatus: ResourceAiProcessingStatus;
  updatedAt?: number;
  youtubeVideoId?: string;
  googleDriveResourceFile?: GoogleDriveResourceFile;
  website?: Website;
  shopifyResourceConfig?: ShopifyResourceConfig;
  tags: string[];
  lastSync?: number;
  syncFrequencyMs?: number;
}

export interface ResourceWithShopifyStore extends Omit<Resource, 'shopifyResourceConfig'> {
  shopifyResourceConfig: ShopifyResourceConfigWithStore;
}

export interface UpdateResourceFileDto {
  resourceId: string;
  fileWithPath?: FileWithPath;
}

export interface UpdateResourceInfoDto {
  resourceId: string;
  teams?: string[];
  tags?: string[];
  syncFrequency?: SyncFrequency;
}

export interface UnUploadedResource {
  fileWithPath?: FileWithPath;
  youtubeVideo?: YoutubeVideo;
  website?: Website;
  googleDriveFile?: GoogleDriveFileInfo;
  shopifyStoreId?: string;
  type: ResourceType;
  fileName: string;
  syncFrequency?: SyncFrequency;
  tags?: string[];
}

export interface BatchResourceUploadDto {
  resources: UnUploadedResource[];
  addResources?: (resources: Resource[], override: boolean) => void;
  notificationId?: string;
  notificationTitleKey?: string;
}

export interface UploadResourceFinalEvent {
  resource: Resource;
  type: UploadResourceEventType;
}

export interface UploadResourceProgressEvent {
  resourceFileName: string;
  resourceType: ResourceType;
  resource?: Resource;
  progress: number;
  type: UploadResourceEventType;
  failed?: boolean;
}

export interface UploadResourceErrorEvent {
  error: ServerError;
  type: UploadResourceEventType;
}

export enum UploadResourceEventType {
  PROGRESS = 'progress',
  FINAL = 'final',
  ERROR = 'error'
}

export enum SyncFrequency {
  ONE_DAY = 'ONE_DAY',
  THREE_DAYS = 'THREE_DAYS',
  SEVEN_DAYS = 'SEVEN_DAYS',
  ONE_MONTH = 'ONE_MONTH',
  NEVER = 'NEVER'
}

export const syncFrequencyLabels = {
  [SyncFrequency.NEVER]: i18n.t('BATCH_RESOURCE_SELECTOR.SYNC.FREQUENCY.NEVER'),
  [SyncFrequency.ONE_DAY]: i18n.t('BATCH_RESOURCE_SELECTOR.SYNC.FREQUENCY.ONE_DAY'),
  [SyncFrequency.THREE_DAYS]: i18n.t('BATCH_RESOURCE_SELECTOR.SYNC.FREQUENCY.THREE_DAYS'),
  [SyncFrequency.SEVEN_DAYS]: i18n.t('BATCH_RESOURCE_SELECTOR.SYNC.FREQUENCY.SEVEN_DAYS'),
  [SyncFrequency.ONE_MONTH]: i18n.t('BATCH_RESOURCE_SELECTOR.SYNC.FREQUENCY.ONE_MONTH')
};

export enum QuerySourceType {
  PAGE = 'page',
  TIME = 'time',
  NONE = 'none'
}
